import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClosedownJobDetail,
  PurchaseOrder,
  UpgradeItemType,
  UpgradeLabourType,
  UpgradeLineItem,
  UpliftRequest
} from '@app/models/closedown-detail.model';
import { AuthService } from '@app/services/auth/auth.service';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { firstValueFrom, forkJoin, of, switchMap } from 'rxjs';
import {
  UpgradeLineItemDialogComponent,
  UpgradeLineItemDialogData
} from './upgrade-line-item-dialog.component';

@Component({
  selector: 'app-job-upgrade',
  templateUrl: './job-upgrade.component.html',
  styleUrls: ['./job-upgrade.component.scss'],
  standalone: false
})
export class JobUpgradeComponent implements OnInit, OnDestroy {
  readonly form = this.formBuilder.group({
    contactName: ['', Validators.required],
    contactNumber: ['', [Validators.required, Validators.pattern(/^[0-9]+$/), Validators.minLength(8), Validators.maxLength(20)]],
    descriptionOfWorks: ['', Validators.required]
  });

  job?: ClosedownJobDetail;
  purchaseOrder: PurchaseOrder | null = null;
  itemTypes: UpgradeItemType[] = [];
  labourTypes: UpgradeLabourType[] = [];
  lineItems: UpgradeLineItem[] = [];
  attachments: File[] = [];
  siteId = '';
  siteName = '';
  contractorName = '';
  jobType = '';
  jobId = '';
  currency = '$';
  upgradeThreshold = 500;
  taxRate = 0.1;
  isLoading = true;
  isSaving = false;
  loadErrorKey = '';
  submitErrorKey = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly invoiceService: InvoiceService,
    private readonly jobService: JobService,
    private readonly navigationService: NavigationService,
    private readonly authService: AuthService
  ) {
    this.navigationService.onChange(false, false);
  }

  get jobTypeKey(): string {
    const keys: Record<string, string> = {
      NEW: 'open_jobs_summary_new',
      'ON-SITE': 'open_jobs_summary_on_site',
      RETURNING: 'open_jobs_summary_returning',
      'AWAITING-PARTS': 'open_jobs_summary_awaiting_parts',
      PPM: 'open_jobs_summary_ppm'
    };
    return keys[this.jobType.toUpperCase()] || 'open_jobs_summary_title';
  }

  ngOnDestroy(): void {
    this.navigationService.onChange(true, false);
  }

  get totalTax(): number {
    return this.round(this.lineItems.reduce((total, item) => total + item.lineTax, 0));
  }

  get totalGross(): number {
    return this.round(this.lineItems.reduce((total, item) => total + item.lineGross, 0));
  }

  get totalNet(): number {
    return this.round(this.totalGross - this.totalTax);
  }

  get canSubmit(): boolean {
    return this.form.valid
      && this.lineItems.length > 0
      && this.totalGross > this.upgradeThreshold
      && !this.isSaving;
  }

  ngOnInit(): void {
    this.siteId = this.route.snapshot.paramMap.get('siteId') || '';
    this.jobType = this.route.snapshot.paramMap.get('jobType') || '';
    this.jobId = this.route.snapshot.paramMap.get('jobId') || '';
    this.siteName = this.route.snapshot.queryParamMap.get('siteName') || '';

    if (!this.siteId || !this.jobType || !this.jobId) {
      this.loadErrorKey = 'job_upgrade_error_missing_details';
      this.isLoading = false;
      return;
    }

    this.jobService.getJob(this.jobId).pipe(
      switchMap(job => forkJoin({
        job: of(job),
        user: this.authService.getUser(),
        purchaseOrder: this.invoiceService.getUpliftPurchaseOrder(this.jobId, job.companyId),
        itemTypes: this.invoiceService.getUpgradeItemTypes(),
        labourTypes: this.invoiceService.getUpgradeLabourTypes(),
        //settings: this.invoiceService.getUpgradeStoreTypeSettings()
      })),
      switchMap(result => {
        if (!result.purchaseOrder?.id) {
          throw new Error('po-unavailable');
        }
        if (!this.canCreateUplift(result.purchaseOrder)) {
          throw new Error('upgrade-exists');
        }

        this.job = result.job;
        this.purchaseOrder = result.purchaseOrder;
        this.siteName = this.siteName || result.job.site?.name || '';
        this.contractorName = result.user?.companies?.find(company => company.id === result.job.companyId)?.name || '';
        this.itemTypes = result.itemTypes;
        this.labourTypes = result.labourTypes;
        const setting = null;
        //const setting = result.settings.find(item => item.storeTypeId === result.job.storeTypeId)
        //  || result.settings.find(item => item.storeTypeId === 0);
        this.currency = '$';
        this.upgradeThreshold = 500;

        if (!result.job.siteId) {
          throw new Error('missing-store');
        }

        // return this.invoiceService.getUpgradeTaxRate(result.job.siteId);
        return of(this.taxRate);
      })
    ).subscribe({
      next: taxRate => {
        this.taxRate = Number(taxRate) || 0;
        this.isLoading = false;
      },
      error: error => {
        if (error instanceof Error && error.message === 'po-unavailable') {
          this.loadErrorKey = 'job_upgrade_error_po_unavailable';
        } else {
          this.loadErrorKey = error instanceof Error && error.message === 'upgrade-exists'
            ? 'job_upgrade_error_already_exists'
            : 'job_upgrade_error_load';
        }
        this.isLoading = false;
      }
    });
  }

  openLineItem(lineItem?: UpgradeLineItem): void {
    const data: UpgradeLineItemDialogData = {
      itemTypes: this.itemTypes,
      labourTypes: this.labourTypes,
      taxRate: this.taxRate,
      currency: this.currency,
      lineItem
    };
    const dialogRef = this.dialog.open(UpgradeLineItemDialogComponent, {
      width: '38rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100dvh - 2rem)',
      disableClose: true,
      data
    });

    dialogRef.afterClosed().subscribe((result?: UpgradeLineItem) => {
      if (!result) {
        return;
      }

      const index = lineItem ? this.lineItems.indexOf(lineItem) : -1;
      this.lineItems = index >= 0
        ? this.lineItems.map((item, itemIndex) => itemIndex === index ? result : item)
        : [...this.lineItems, result];
    });
  }

  deleteLineItem(lineItem: UpgradeLineItem): void {
    this.lineItems = this.lineItems.filter(item => item !== lineItem);
  }

  selectAttachments(event: Event): void {
    const input = event.target as HTMLInputElement;
    const existingNames = new Set(this.attachments.map(file => file.name));
    this.attachments = [
      ...this.attachments,
      ...Array.from(input.files || []).filter(file => !existingNames.has(file.name))
    ];
    input.value = '';
  }

  removeAttachment(fileToRemove: File): void {
    this.attachments = this.attachments.filter(file => file !== fileToRemove);
  }

  goBack(): void {
    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType, this.jobId],
      { queryParams: { siteName: this.siteName } }
    );
  }

  print(): void {
    window.print();
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    this.submitErrorKey = '';

    if (!this.canSubmit || !this.job || !this.job.siteId || !this.job.companyId) {
      return;
    }

    if (this.attachments.length > 0 && this.job.storeTypeId === undefined) {
      this.submitErrorKey = 'job_upgrade_error_attachments';
      return;
    }

    this.isSaving = true;
    try {
      const purchaseOrder = await firstValueFrom(
        this.invoiceService.getUpliftPurchaseOrder(this.jobId, this.job.companyId)
      );
      if (!purchaseOrder?.id) {
        this.submitErrorKey = 'job_upgrade_error_po_unavailable';
        return;
      }
      if (!this.canCreateUplift(purchaseOrder)) {
        this.submitErrorKey = 'job_upgrade_error_already_exists';
        return;
      }

      const values = this.form.getRawValue();
      const request: UpliftRequest = {
        companyId: this.job.companyId,
        contactName: values.contactName!.trim(),
        contactNumber: values.contactNumber!.trim(),
        jobId: this.jobId,
        purchaseOrderId: purchaseOrder.id,
        notes: values.descriptionOfWorks!.trim(),
        amountExcludingTax: this.totalNet,
        currencyCode: purchaseOrder?.currencyCode || this.currency,
        upliftRequestLines: this.lineItems.map(item => ({
          itemID: item.itemID,
          itemCode: item.itemCode,
          ...(item.itemUnitOfMeasure
            ? { itemUnitOfMeasure: item.itemUnitOfMeasure }
            : {}),
          quantity: item.itemQuantity,
          unitPriceExcludingTax: item.unitPrice
        }))
      };

      if (this.attachments.length > 0) {
        const files = await Promise.all(this.attachments.map(async file => ({
          data: await this.readFile(file),
          fileName: file.name,
          parentType: 'JobEvent' as const,
          parentId: this.jobId,
          fileUploadTypeCode: 'UD' as const,
          mimeType: file.type
        })));
        await firstValueFrom(this.jobService.uploadFiles({
          storeTypeId: this.job.storeTypeId!,
          fileDirectory: `JobEvent-${this.jobId}`,
          files,
          siteId: this.job.siteId
        }));
      }

      await firstValueFrom(this.invoiceService.createUpliftRequest(request));
      this.goBack();
    } catch {
      this.submitErrorKey = 'job_upgrade_error_submit';
    } finally {
      this.isSaving = false;
    }
  }

  private round(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private canCreateUplift(purchaseOrder: PurchaseOrder | null): boolean {
    return !!purchaseOrder
      && purchaseOrder.upliftRequests.length === 0
      && purchaseOrder.uplifts.length === 0;
  }

  private readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || '').split(',').pop() || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
}